import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { SiteHeader, PBOLogo } from "@/components/Logo";
import {
  KeyRound,
  Link as LinkIcon,
  RefreshCw,
  LifeBuoy,
  Crown,
  ArrowRight,
  Eye,
  Smartphone,
  Bookmark,
  AlertCircle,
  DollarSign,
  Trophy,
  BarChart3,
} from "lucide-react";

export default function Help() {
  return (
    <div className="min-h-screen bg-pbo-cream">
      <SiteHeader
        rightSlot={
          <Link
            to="/"
            className="text-sm font-semibold text-pbo-green hover:text-pbo-gold transition-colors"
            data-testid="header-home-link"
          >
            ← Back to the clubhouse
          </Link>
        }
      />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        {/* Hero */}
        <div className="grid sm:grid-cols-[auto_1fr] gap-6 items-center mb-10">
          <PBOLogo size={96} />
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] font-semibold text-pbo-gold">
              Help · Rejoin a draft
            </div>
            <h1 className="font-serif text-pbo-green text-4xl sm:text-5xl mt-2 leading-tight">
              Getting back into your draft.
            </h1>
            <p className="text-pbo-ink/65 mt-3 text-sm leading-relaxed">
              Three easy ways to pick up exactly where you left off — plus a few
              backup plans for when life happens.
            </p>
          </div>
        </div>

        <div className="gold-rule mb-10" />

        {/* The three ways */}
        <section className="space-y-5 mb-12">
          <h2 className="font-serif text-pbo-green text-2xl mb-2">
            The 3 ways to get back in
          </h2>

          <Card
            n="01"
            icon={<RefreshCw className="w-5 h-5" />}
            title="Same browser, same device"
            tag="Easiest"
          >
            <p>
              Just go to{" "}
              <CodeLink to="/">draft.palmbreezyopen.com</CodeLink> in the same
              browser you used to join. It remembers you automatically and drops
              you right back into the lobby (or live auction) where you left off.
            </p>
          </Card>

          <Card
            n="02"
            icon={<KeyRound className="w-5 h-5" />}
            title="Your personal seat link"
            tag="Works from any device"
          >
            <p>
              When you first joined, the lobby showed a{" "}
              <strong className="text-pbo-green">Save your seat</strong> card.
              That link looks like:
            </p>
            <code className="block bg-pbo-paper border border-pbo-border rounded-lg px-3 py-2 mt-2 text-xs font-mono text-pbo-green break-all">
              draft.palmbreezyopen.com/draft/<span className="text-pbo-gold">ABCD</span>?as=<span className="text-pbo-gold">…</span>
            </code>
            <p className="mt-3">
              Open it on <em>any</em> browser, phone, or computer — you&apos;ll be
              instantly recognized as yourself, with your full team intact.
            </p>
            <div className="mt-3 p-3 bg-pbo-gold/10 border border-pbo-gold/30 rounded-lg flex items-start gap-2 text-xs text-pbo-ink/85">
              <Bookmark className="w-4 h-4 text-pbo-gold shrink-0 mt-0.5" />
              <span>
                <strong>The moment you join a new draft</strong>, tap{" "}
                <em>Copy</em> on the &quot;Save your seat&quot; card and paste it
                into Notes or text it to yourself. On phones, tap{" "}
                <em>Text it to me</em> and the Messages app opens pre-filled.
              </span>
            </div>
          </Card>

          <Card
            n="03"
            icon={<LinkIcon className="w-5 h-5" />}
            title="Your name + the room code"
            tag="Fallback"
          >
            <p>
              Go to{" "}
              <code className="bg-pbo-paper border border-pbo-border rounded px-1.5 py-0.5 text-xs font-mono text-pbo-green">
                draft.palmbreezyopen.com/join/<span className="text-pbo-gold">ABCD</span>
              </code>{" "}
              (or tap the original invite link) and enter the{" "}
              <strong>exact same display name</strong> you used the first time.
            </p>
            <p className="mt-2">
              Capitalization doesn&apos;t matter — &quot;Tiger&quot; = &quot;tiger&quot; =
              &quot;TIGER&quot;. You&apos;ll see a{" "}
              <span className="text-pbo-green font-semibold">
                Welcome back, &lt;name&gt;
              </span>{" "}
              toast and land in the lobby.
            </p>
          </Card>
        </section>

        {/* Troubleshooting */}
        <section className="bg-white border border-pbo-border rounded-2xl p-6 mb-12">
          <h2 className="font-serif text-pbo-green text-2xl flex items-center gap-2 mb-4">
            <LifeBuoy className="w-5 h-5 text-pbo-gold" />
            If none of those work
          </h2>
          <ul className="space-y-3 text-sm">
            <Trouble
              icon={<AlertCircle className="w-4 h-4" />}
              title="Forgot what name you used?"
            >
              Text the host. They can see your name in their participant list and
              either tell you what it was, <em>or</em> tap the 🔗 icon next to your
              name to copy your personal seat link and text it to you.
            </Trouble>
            <Trouble
              icon={<Eye className="w-4 h-4" />}
              title="Listed as a Spectator instead of a Drafter?"
            >
              Ask the host to tap <strong>Add to draft</strong> next to your name
              in the lobby. (This only works <em>before</em> the draft starts.)
            </Trouble>
            <Trouble
              icon={<Smartphone className="w-4 h-4" />}
              title="Want one-tap access in the future?"
            >
              From the homepage on your phone, look for the{" "}
              <strong>&quot;Add Palm Breezy Open to your home screen&quot;</strong>{" "}
              prompt and install it — it&apos;ll behave like a regular app.
            </Trouble>
          </ul>
        </section>

        {/* Host cheat-sheet */}
        <section className="bg-pbo-green text-pbo-cream rounded-2xl p-6 sm:p-8 mb-12 relative overflow-hidden">
          <div className="absolute inset-0 pbo-dotted opacity-25" />
          <div className="relative">
            <h2 className="font-serif text-2xl flex items-center gap-2 mb-4">
              <Crown className="w-5 h-5 text-pbo-gold-soft" />
              Host&apos;s quick reference
            </h2>
            <div className="space-y-3 text-sm">
              <HostRow
                situation="Someone says: &quot;I&apos;m on my iPad now, my session is gone.&quot;"
                action="Tap the 🔗 icon next to their name in your lobby → text them the link."
              />
              <HostRow
                situation="Someone shows up twice (typo'd their name)."
                action="Move the duplicate to Watching, then tell them to rejoin using the first spelling."
              />
              <HostRow
                situation="Someone joined late and needs to draft."
                action="Tap Add to draft next to them in the Watching list."
              />
              <HostRow
                situation="You're switching browsers/devices yourself."
                action="Same drill — use YOUR seat link from the lobby. It works for the host too."
              />
            </div>
          </div>
        </section>

        {/* --- Salary Cap Contest section --- */}
        <section className="mb-12 mt-12">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-12 h-12 rounded-xl bg-pbo-gold/15 text-pbo-gold grid place-items-center">
              <DollarSign className="w-6 h-6" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-pbo-gold">
                Mode guide
              </div>
              <h2 className="font-serif text-pbo-green text-2xl sm:text-3xl">
                Salary Cap Contest
              </h2>
            </div>
          </div>

          <p className="text-pbo-ink/75 leading-relaxed mb-5">
            Async, DraftKings-style alternative to the live auction. The host
            sets a salary cap and assigns each golfer a price. Friends quietly
            build a team under cap before the lock time. Scores sync from your
            Google Sheet.
          </p>

          <div className="space-y-3">
            <Card
              n="01"
              icon={<DollarSign className="w-5 h-5" />}
              title="Host sets the field"
              tag="Pre-lock"
            >
              On <em>Host a contest</em>, set the cap, roster size (golfers per
              team), and an optional lock time. Edit any of these later from{" "}
              <strong>Edit settings</strong>. Add golfers with name + salary —
              or click <strong>Load saved list</strong> to reuse a list you
              built before (lives in your browser).
            </Card>

            <Card
              n="02"
              icon={<KeyRound className="w-5 h-5" />}
              title="Friends join with the 4-letter code"
              tag="Anyone"
            >
              Same flow as the auction draft. They pick a display name, see the
              field with salaries, and tap{" "}
              <strong className="text-pbo-green">Pick</strong> to build a
              roster. Picks stay <strong>private</strong> until lock. When
              they&apos;re happy, they tap{" "}
              <strong>Submit picks</strong> — and can{" "}
              <strong>Edit picks</strong> any time before lock.
            </Card>

            <Card
              n="03"
              icon={<BarChart3 className="w-5 h-5" />}
              title="Scoring sync from Google Sheets"
              tag="Host"
            >
              In <strong>Edit settings</strong>, you can paste one Google
              Sheets link per round (R1, R2, R3…). Each sheet/tab needs two
              columns: <strong>Golfer Name</strong> in column A and{" "}
              <strong>Points</strong> in column B. Names must match the
              spelling in your contest pool (case-insensitive).
              <br />
              <br />
              <strong>Best URL form for live updates:</strong> use the regular
              sheet edit URL with <code className="bg-pbo-paper px-1 rounded text-[12px]">#gid=...</code>{" "}
              on the end (the gid points to the specific tab). We auto-convert
              it into a live <code className="bg-pbo-paper px-1 rounded text-[12px]">/export?format=csv</code>{" "}
              call — no 5-minute cache delay. Make sure your sheet is shared as{" "}
              <em>Anyone with the link → Viewer</em>.
              <br />
              <br />
              You can also customize round labels (e.g. rename R4 to{" "}
              <strong>Bonus</strong>) — the label shows everywhere on the
              scoreboard.
              <br />
              <br />
              The app auto-refreshes every 30 seconds. Hit{" "}
              <strong>🔄 Sync sheet</strong> in the header to force an
              immediate refresh, or use <strong>Test all rounds</strong> in
              settings to verify the connection (shows how many golfers
              matched per round).
            </Card>

            <Card
              n="04"
              icon={<Trophy className="w-5 h-5" />}
              title="Standings + public leaderboard"
              tag="Anyone"
            >
              Once locked, the lobby shows live standings ranked by total
              points. Round-by-round chips (e.g.{" "}
              <span className="font-mono">R1 +17 · R2 +5 · R3 +8 · Bonus +3</span>) appear
              next to each team. Tap a row to expand and see per-golfer, per-round
              breakdown. There&apos;s also a public link at{" "}
              <code className="bg-pbo-paper px-1 rounded text-[12px]">/contest/CODE/standings</code>{" "}
              — anyone can bookmark it to check scores. Copy it from the{" "}
              <strong>Copy public link</strong> button.
            </Card>

            <Card
              n="05"
              icon={<Bookmark className="w-5 h-5" />}
              title="Saved lists"
              tag="Browser-only"
            >
              In the contest lobby (pre-lock), use{" "}
              <strong>💾 Save current list</strong> to store the whole field
              with salaries to your browser. Every future contest you create on
              that same browser shows the list in{" "}
              <strong>📂 Load saved list</strong>. To move lists between
              browsers/devices, use the new{" "}
              <strong>Export</strong> / <strong>Import</strong> buttons in the
              Load dialog (download a `.json`, drop it into another browser).
            </Card>
          </div>
        </section>

        <div className="text-center">
          <Link to="/">
            <Button
              className="bg-pbo-green text-pbo-cream hover:bg-pbo-green-deep h-12 px-6 rounded-full font-semibold"
              data-testid="help-back-home-btn"
            >
              Back to the clubhouse
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>

      <footer className="pb-10 text-center text-xs text-pbo-muted">
        <span className="font-script text-pbo-gold text-base">cheers</span>
      </footer>
    </div>
  );
}

const Card = ({ n, icon, title, tag, children }) => (
  <div className="bg-white border border-pbo-border rounded-2xl p-6">
    <div className="flex items-start gap-4 mb-3">
      <span className="w-10 h-10 rounded-xl bg-pbo-green text-pbo-cream grid place-items-center shrink-0">
        {icon}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-mono text-[10px] text-pbo-muted">{n}</span>
          <h3 className="font-serif text-pbo-green text-xl leading-tight">
            {title}
          </h3>
          {tag && (
            <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-pbo-gold">
              {tag}
            </span>
          )}
        </div>
      </div>
    </div>
    <div className="text-sm text-pbo-ink/80 leading-relaxed pl-14">
      {children}
    </div>
  </div>
);

const CodeLink = ({ to, children }) => (
  <Link
    to={to}
    className="bg-pbo-paper border border-pbo-border rounded px-1.5 py-0.5 text-xs font-mono text-pbo-green hover:bg-pbo-green hover:text-pbo-cream transition-colors"
  >
    {children}
  </Link>
);

const Trouble = ({ icon, title, children }) => (
  <li className="flex items-start gap-3">
    <span className="w-7 h-7 rounded-lg bg-pbo-gold/15 text-pbo-gold grid place-items-center shrink-0">
      {icon}
    </span>
    <div className="flex-1">
      <div className="font-semibold text-pbo-green">{title}</div>
      <div className="text-pbo-ink/75 leading-relaxed mt-0.5">{children}</div>
    </div>
  </li>
);

const HostRow = ({ situation, action }) => (
  <div className="border-b border-pbo-gold/15 pb-2.5 last:border-b-0">
    <div className="text-pbo-cream/85 text-[13px]">{situation}</div>
    <div className="text-pbo-gold-soft text-[12px] mt-1 leading-relaxed">
      → {action}
    </div>
  </div>
);
